import React from "react";
import { useBrand } from "@webuild-ai/react-ui/hooks";

import { useTheme } from "@/hooks/use-theme";
import { getReadableTextColor } from "@/utils/color";

/**
 * Opacity of the send button's drop shadow, expressed as a percentage of the
 * brand primary mixed into transparency. Low enough to read as a lift rather
 * than a second, blurrier button.
 */
const SHADOW_MIX = 33;

/**
 * The brand-coloured inline style for a chat send button that is ready to
 * submit, or `undefined` when it is not.
 *
 * HeroUI's `variant="primary"` paints its own theme primary, which is not the
 * active tenant's blue, so the send button has to be given the brand colour
 * explicitly. The derivation used to sit inline in `ChatInputActions`, and the
 * Workflow Builder's composer (`BuildChatComposer`) shipped without it, which
 * is how LSEG came to have a brand-blue send button in the chat and a generic
 * one in the builder. It lives here for the same reason `useBrandAccent` does:
 * so the treatment cannot be corrected in one composer and forgotten in the
 * next.
 *
 * The foreground is derived rather than taken from the pack's `onPrimary`
 * role, because a tenant may publish a light primary and `getReadableTextColor`
 * is what keeps the arrow legible on it. The icon inherits it through
 * `currentColor`.
 *
 * ⚠️ This hook is not the only thing painting these buttons, and it does not
 * win everywhere. `heroui-v2-compat.css` carries a `background-color` with
 * `!important`, matched on each composer's `data-id`, which has to outrank
 * HeroUI's layered variant styles and therefore also outranks the
 * `backgroundColor` returned here, since an important author declaration beats
 * a normal inline one. That rule is the authority for the background and for
 * every state this hook returns nothing for, namely the resting and streaming
 * buttons. What the hook uniquely supplies is the contrast-safe foreground and
 * the ready-state lift. `backgroundColor` is kept rather than dropped because
 * the compat stylesheet is explicitly temporary, so the hook has to stand on
 * its own the day that file goes. Two consequences follow for anyone editing
 * either side. Adding a composer means adding its `data-id` to that rule as
 * well as calling this hook, or the new button renders HeroUI's theme primary.
 * And putting `!important` back on that rule's `color` would silently disable
 * `getReadableTextColor` again, which is the bug LSEG-284 found.
 *
 * @param isReady - True when the button would submit, meaning a prompt is
 *   present, nothing is streaming and no upload is in flight. It must be the
 *   exact negation of the caller's own `isDisabled`, or a button the user
 *   cannot press carries the treatment that says they can.
 */
export const useSendButtonStyle = (isReady: boolean): React.CSSProperties | undefined => {
  const { brand } = useBrand();
  const { isDark } = useTheme();

  const primary = isDark ? brand.colours.dark.primary : brand.colours.light.primary;

  return React.useMemo(() => {
    if (!isReady) return undefined;

    return {
      backgroundColor: primary,
      color: getReadableTextColor(primary),
      boxShadow: `0 2px 8px -2px color-mix(in srgb, ${primary} ${SHADOW_MIX}%, transparent)`,
    };
  }, [isReady, primary]);
};
